<template>
  <div class="employee-files">
    <v-card outlined class="info-card">
      <v-card-title class="info-title d-flex align-center">
        <v-icon size="18" class="mr-2">mdi-folder-account</v-icon>
        {{ $t("employeeFiles.title") }}
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="tonal" @click="openUploadDialog" :disabled="!canUpload">
          <v-icon start size="18">mdi-upload</v-icon>
          {{ $t("employeeFiles.actions.upload") }}
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-alert v-if="!effectiveEmployeeId" type="warning" density="comfortable" class="mb-4">
          {{ $t("employeeFiles.errors.missingEmployee") }}
        </v-alert>

        <v-alert v-if="loadError" type="error" density="comfortable" class="mb-4">
          {{ loadError }}
        </v-alert>

        <div class="filters mb-4">
          <v-select
            v-model="selectedCategory"
            :items="categoryFilterItems"
            item-title="label"
            item-value="value"
            :label="$t('employeeFiles.filters.category')"
            hide-details
            clearable
            class="filter-select"
            :disabled="loading || !files.length"
          ></v-select>
          <v-text-field
            v-model="searchTerm"
            :label="$t('employeeFiles.filters.search')"
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-field"
            :disabled="loading || !files.length"
            clearable
          ></v-text-field>
        </div>

        <div v-if="loading" class="d-flex align-center justify-center py-6">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else-if="!files.length" class="empty-state">
          <v-icon size="36" color="primary" class="mb-2">mdi-file-document-outline</v-icon>
          <div class="text-subtitle-1 mb-1">{{ $t("employeeFiles.empty.title") }}</div>
          <div class="text-body-2 text-medium-emphasis">{{ $t("employeeFiles.empty.subtitle") }}</div>
        </div>

        <v-row v-else class="file-grid" dense>
          <v-col
            v-for="file in filteredFiles"
            :key="file.id"
            cols="12"
            md="6"
            class="file-col"
          >
            <v-card outlined class="file-card">
              <v-card-text class="file-card__content">
                <div class="file-card__top">
                  <div class="d-flex align-center">
                    <v-avatar color="#fdecea" size="44" class="mr-3">
                      <v-icon color="#e63946">mdi-file-document-outline</v-icon>
                    </v-avatar>
                    <div>
                      <div class="d-flex align-center flex-wrap">
                        <span class="file-name mr-2">{{ file.filename }}</span>
                        <v-chip color="primary">
                          {{ file.categoryLabel || $t("employeeFiles.labels.noCategory") }}
                        </v-chip>
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ formatSize(file.size) }} · {{ formatDate(file.createdAt) }}
                      </div>
                    </div>
                  </div>
                  <div class="file-card__actions">
                    <v-btn icon variant="text" size="small" @click="handlePreview(file)" :title="$t('employeeFiles.actions.preview')">
                      <v-icon size="18">mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn icon variant="text" size="small" @click="handleDownload(file)" :title="$t('employeeFiles.actions.download')">
                      <v-icon size="18">mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canEdit(file)"
                      icon
                      variant="text"
                      size="small"
                      @click="openEditDialog(file)"
                      :title="$t('employeeFiles.actions.edit')"
                    >
                      <v-icon size="18">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canDelete"
                      icon
                      variant="text"
                      size="small"
                      color="error"
                      @click="openDeleteDialog(file)"
                      :title="$t('employeeFiles.actions.delete')"
                    >
                      <v-icon size="18">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div v-if="file.description" class="text-body-2 text-medium-emphasis mt-2">
                  {{ file.description }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="uploadDialog.open" max-width="560">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon size="18" class="mr-2">mdi-upload</v-icon>
          {{ $t("employeeFiles.actions.upload") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert
            v-if="uploadDialog.error"
            type="error"
            density="comfortable"
            class="mb-3"
          >
            {{ uploadDialog.error }}
          </v-alert>
          <div
            class="dropzone mb-3"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="onFileDrop"
            @click="triggerFileInput"
          >
            <v-icon size="36" color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
            <div class="text-subtitle-2">{{ $t("employeeFiles.upload.dropTitle") }}</div>
            <div class="text-caption text-medium-emphasis">
              {{ $t("employeeFiles.upload.dropDescription") }}
              {{ $t("employeeFiles.upload.selectedFile", { name: uploadDialog.fileName || "—" }) }}
            </div>
            <input
              ref="fileInput"
              type="file"
              class="d-none"
              :accept="acceptExtensions"
              @change="onFileSelect"
            />
          </div>

          <v-combobox
            v-model="uploadDialog.categoryInput"
            :items="categoryOptions"
            :label="$t('employeeFiles.form.category')"
            :hint="$t('employeeFiles.form.categoryHint')"
            persistent-hint
            clearable
            hide-details
            class="mb-3"
            :return-object="false"
          ></v-combobox>

          <v-textarea
            v-model="uploadDialog.description"
            :label="$t('employeeFiles.form.description')"
            auto-grow
            rows="2"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeUploadDialog">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="uploadDialog.loading" @click="submitUpload" :disabled="!effectiveEmployeeId">
            {{ $t("employeeFiles.actions.upload") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog.open" max-width="520">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon size="18" class="mr-2">mdi-pencil</v-icon>
          {{ $t("employeeFiles.edit.title") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert
            v-if="editDialog.error"
            type="error"
            density="comfortable"
            class="mb-3"
          >
            {{ editDialog.error }}
          </v-alert>
          <div class="text-body-2 mb-2 font-weight-medium">{{ editDialog.file?.filename }}</div>
          <v-combobox
            v-model="editDialog.categoryInput"
            :items="categoryOptions"
            :label="$t('employeeFiles.form.category')"
            :hint="$t('employeeFiles.form.categoryHint')"
            persistent-hint
            clearable
            hide-details
            class="mb-3"
            :return-object="false"
          ></v-combobox>
          <v-textarea
            v-model="editDialog.description"
            :label="$t('employeeFiles.form.description')"
            auto-grow
            rows="2"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeEditDialog">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="editDialog.loading" @click="submitEdit">{{ $t("app.actions.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon size="18" class="mr-2" color="error">mdi-alert</v-icon>
          {{ $t("employeeFiles.delete.title") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          {{
            $t("employeeFiles.delete.description", {
              name: deleteDialog.file?.filename || ""
            })
          }}
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeDeleteDialog">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="error" :loading="deleteDialog.loading" @click="confirmDelete">{{ $t("app.actions.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { formatDisplayDate } from "@/Utile/displayFormat.mjs";

const MAX_SIZE_BYTES = 30 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".odt", ".csv", ".jpg", ".jpeg", ".png", ".gif", ".webp"];
const PRIVILEGED_ROLES = new Set(["admin", "miniadmin", "hr"]);
const DEFAULT_CATEGORIES = ["Contracte", "Documente Personale", "Certificări"];

const parseId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export default defineComponent({
  name: "EmployeeFiles",
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props) {
    const { t, locale } = useI18n();

    const files = ref([]);
    const categories = ref([]);
    const loading = ref(false);
    const loadError = ref("");
    const selectedCategory = ref(null);
    const searchTerm = ref("");
    const fileInput = ref(null);

    const uploadDialog = ref({
      open: false,
      loading: false,
      error: "",
      file: null,
      fileName: "",
      categoryInput: "",
      description: "",
    });

    const editDialog = ref({
      open: false,
      loading: false,
      error: "",
      file: null,
      categoryInput: "",
      description: "",
    });

    const deleteDialog = ref({
      open: false,
      loading: false,
      file: null,
    });

    const userRole = ref((localStorage.getItem("user-role") || "").toLowerCase());
    const userCompanyId = ref(parseId(localStorage.getItem("user-company-id")));

    const effectiveEmployeeId = computed(() => {
      const fromProps = parseId(props.employeeId);
      if (fromProps) return fromProps;
      const storedHrSelection = parseId(localStorage.getItem("hr-dashboard:selected-employee-id"));
      if (storedHrSelection) return storedHrSelection;
      return parseId(localStorage.getItem("user-employee-id"));
    });
    const canDelete = computed(() => PRIVILEGED_ROLES.has(userRole.value));
    const canUpload = computed(() => Boolean(effectiveEmployeeId.value));

    const categoryOptions = computed(() => {
      const dynamic = categories.value.map((cat) => cat.label);
      const merged = [...DEFAULT_CATEGORIES, ...dynamic].filter(Boolean);
      return Array.from(new Set(merged));
    });
    const categoryFilterItems = computed(() => {
      const counts = new Map();
      files.value.forEach((file) => {
        const label = file.categoryLabel || null;
        if (!label) return;
        counts.set(label, (counts.get(label) || 0) + 1);
      });

      const items = Array.from(counts.entries())
        .filter(([label]) => Boolean(label))
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, count]) => ({ label: `${label} (${count})`, value: label }));

      return [{ label: t("employeeFiles.filters.allCategories"), value: null }, ...items];
    });

    const filteredFiles = computed(() => {
      let list = files.value;
      if (selectedCategory.value) {
        list = list.filter((file) => file.categoryLabel === selectedCategory.value);
      }
      if (searchTerm.value) {
        const needle = searchTerm.value.trim().toLowerCase();
        list = list.filter((file) => {
          const name = (file.filename || "").toLowerCase();
          const desc = (file.description || "").toLowerCase();
          return name.includes(needle) || desc.includes(needle);
        });
      }
      return list;
    });

    const acceptExtensions = computed(() => ALLOWED_EXTENSIONS.join(","));

    const formatSize = (bytes) => {
      const size = Number(bytes || 0);
      if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
      }
      return `${(size / 1024).toFixed(0)} KB`;
    };

    const formatDate = (value) => {
      const date = value ? new Date(value) : null;
      if (!date || Number.isNaN(date.getTime())) return "—";
      return formatDisplayDate(date);
    };

    const resetUploadState = () => {
      uploadDialog.value.error = "";
      uploadDialog.value.file = null;
      uploadDialog.value.fileName = "";
      uploadDialog.value.categoryInput = "";
      uploadDialog.value.description = "";
    };

    const openUploadDialog = () => {
      resetUploadState();
      uploadDialog.value.open = true;
    };

    const closeUploadDialog = () => {
      uploadDialog.value.open = false;
      resetUploadState();
    };

    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.value = "";
        fileInput.value.click();
      }
    };

    const validateFile = (file) => {
      if (!file) return t("employeeFiles.errors.noFile");
      const ext = (file.name || "").toLowerCase().slice(file.name.lastIndexOf("."));
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return t("employeeFiles.errors.invalidType");
      }
      if (file.size > MAX_SIZE_BYTES) {
        return t("employeeFiles.errors.tooLarge");
      }
      return "";
    };

    const onFileSelect = (event) => {
      const file = event.target.files?.[0];
      const error = validateFile(file);
      uploadDialog.value.error = error;
      if (!error) {
        uploadDialog.value.file = file;
        uploadDialog.value.fileName = file.name;
      }
    };

    const onFileDrop = (event) => {
      const file = event.dataTransfer?.files?.[0];
      const error = validateFile(file);
      uploadDialog.value.error = error;
      if (!error) {
        uploadDialog.value.file = file;
        uploadDialog.value.fileName = file.name;
      }
    };

    const fetchFiles = async () => {
      if (!effectiveEmployeeId.value) return;
      loading.value = true;
      try {
        const params = { employeeId: effectiveEmployeeId.value };
        const response = await axios.get("/files", { params });
        files.value = Array.isArray(response.data) ? response.data : [];
        loadError.value = "";
      } catch (error) {
        console.error("Eroare la listarea fișierelor:", error);
        if (error?.response?.status === 404) {
          loadError.value = t("employeeFiles.errors.notFound");
        } else if (error?.response?.status === 403) {
          loadError.value = t("employeeFiles.errors.forbidden");
        } else {
          loadError.value = t("employeeFiles.errors.loadGeneric");
        }
      } finally {
        loading.value = false;
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get("/files/categories", {
          params: { companyId: userCompanyId.value || undefined },
        });
        categories.value = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Eroare la listarea categoriilor:", error);
      }
    };

    const submitUpload = async () => {
      uploadDialog.value.error = "";
      if (!uploadDialog.value.file) {
        uploadDialog.value.error = t("employeeFiles.errors.selectFile");
        return;
      }
      if (!effectiveEmployeeId.value) {
        uploadDialog.value.error = t("employeeFiles.errors.missingEmployee");
        return;
      }
      uploadDialog.value.loading = true;
      try {
        const formData = new FormData();
        formData.append("file", uploadDialog.value.file);
        formData.append("employeeId", effectiveEmployeeId.value);
        if (userCompanyId.value) {
          formData.append("companyId", userCompanyId.value);
        }
        if (uploadDialog.value.categoryInput) {
          formData.append("category", uploadDialog.value.categoryInput);
        }
        if (uploadDialog.value.description) {
          formData.append("description", uploadDialog.value.description);
        }

        await axios.post("/files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        closeUploadDialog();
        await Promise.all([fetchFiles(), fetchCategories()]);
      } catch (error) {
        console.error("Eroare la încărcare:", error);
        uploadDialog.value.error =
          error?.response?.data?.message || t("employeeFiles.errors.uploadFailed");
      } finally {
        uploadDialog.value.loading = false;
      }
    };

    const openEditDialog = (file) => {
      editDialog.value.file = file;
      editDialog.value.categoryInput = file.categoryLabel || "";
      editDialog.value.description = file.description || "";
      editDialog.value.error = "";
      editDialog.value.open = true;
    };

    const closeEditDialog = () => {
      editDialog.value.open = false;
      editDialog.value.loading = false;
      editDialog.value.error = "";
      editDialog.value.file = null;
    };

    const submitEdit = async () => {
      if (!editDialog.value.file) return;
      editDialog.value.loading = true;
      editDialog.value.error = "";
      try {
        await axios.patch(`/files/${editDialog.value.file.id}`, {
          category: editDialog.value.categoryInput || null,
          description: editDialog.value.description || null,
        });
        closeEditDialog();
        await Promise.all([fetchFiles(), fetchCategories()]);
      } catch (error) {
        console.error("Eroare la actualizare:", error);
        editDialog.value.error =
          error?.response?.data?.message || t("employeeFiles.errors.updateFailed");
      } finally {
        editDialog.value.loading = false;
      }
    };

    const canEdit = (file) => {
      if (PRIVILEGED_ROLES.has(userRole.value)) return true;
      const userEmployeeId = parseId(localStorage.getItem("user-employee-id"));
      return userEmployeeId && file.employeeId === userEmployeeId;
    };

    const openDeleteDialog = (file) => {
      deleteDialog.value.file = file;
      deleteDialog.value.loading = false;
      deleteDialog.value.open = true;
    };

    const closeDeleteDialog = () => {
      deleteDialog.value.open = false;
      deleteDialog.value.file = null;
      deleteDialog.value.loading = false;
    };

    const confirmDelete = async () => {
      if (!deleteDialog.value.file) return;
      deleteDialog.value.loading = true;
      try {
        await axios.delete(`/files/${deleteDialog.value.file.id}`);
        closeDeleteDialog();
        await Promise.all([fetchFiles(), fetchCategories()]);
      } catch (error) {
        console.error("Eroare la ștergere:", error);
      } finally {
        deleteDialog.value.loading = false;
      }
    };

    const fetchBlobAndOpen = async (file, mode) => {
      const url = `/files/${file.id}/${mode}`;
      const response = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] || file.mimetype });
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    };

    const handlePreview = async (file) => {
      try {
        const blobUrl = await fetchBlobAndOpen(file, "preview");
        window.open(blobUrl, "_blank", "noopener");
      } catch (error) {
        console.error("Eroare la previzualizare:", error);
      }
    };

    const handleDownload = async (file) => {
      try {
        const blobUrl = await fetchBlobAndOpen(file, "download");
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.filename || t("employeeFiles.labels.document");
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Eroare la descărcare:", error);
      }
    };

    onMounted(async () => {
      await Promise.all([fetchCategories(), fetchFiles()]);
    });

    watch(
      () => effectiveEmployeeId.value,
      async (newId, oldId) => {
        if (!newId || newId === oldId) return;
        await Promise.all([fetchCategories(), fetchFiles()]);
      }
    );

    return {
      files,
      categories,
      loading,
      loadError,
      selectedCategory,
      searchTerm,
      categoryFilterItems,
      filteredFiles,
      formatSize,
      formatDate,
      canDelete,
      canUpload,
      canEdit,
      openUploadDialog,
      closeUploadDialog,
      onFileDrop,
      onFileSelect,
      fileInput,
      triggerFileInput,
      uploadDialog,
      submitUpload,
      categoryOptions,
      acceptExtensions,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
      deleteDialog,
      handlePreview,
      handleDownload,
      openEditDialog,
      closeEditDialog,
      submitEdit,
      editDialog,
      effectiveEmployeeId,
    };
  },
});
</script>

<style scoped>
.employee-files .info-card {
  border-radius: 16px;
}
.file-list {
  padding: 0;
}
.file-item {
  border-bottom: 1px solid #e5e7eb;
}
.file-item:last-of-type {
  border-bottom: none;
}
.file-name {
  font-weight: 600;
}
.filters {
  display: flex;
  gap: 12px;
  align-items: center;
}
.filter-select {
  max-width: 320px;
}
.search-field {
  flex: 1;
  min-width: 240px;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
}
.dropzone {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}
.dropzone:hover {
  border-color: #90caf9;
  background: #f1f5f9;
}
.d-none {
  display: none;
}
.file-grid {
  margin-top: 8px;
}
.file-col {
  margin-bottom: 12px;
}
.file-card {
  border-radius: 12px;
}
.file-card__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.file-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.file-card__actions {
  display: flex;
  gap: 4px;
  align-items: center;
}
</style>
